import { Plus, Trash2 } from 'lucide-react';
import { Button, Input, RadixSelect, RequiredMark } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { RangeRepeatFields } from '@/components/shipping/RangeRepeatFields';
import { RegionSurchargeTable } from '@/components/shipping/RegionSurchargeTable';
import {
  EMS_ZONES,
  isEmsZone,
  type OverseasCountryGroup,
  type OverseasShippingRate,
  type ShippingBasis,
} from '../types/overseasShipping';

interface CountryGroupEditorProps {
  groups: OverseasCountryGroup[];
  basis: ShippingBasis;
  disabled: boolean;
  onChange: (groups: OverseasCountryGroup[]) => void;
}

const emptyRate = (): OverseasShippingRate => ({ weightFromKg: 0, weightToKg: 0, feeKrw: 0 });

const emptyGroup = (): OverseasCountryGroup => ({
  groupName: '',
  conditionName: '',
  countryCodes: [],
  rates: [emptyRate()],
  regionSurcharges: [],
});

/**
 * 범위반복의 기준 구간 — 가장 낮은 구간(시작 무게가 제일 작은 행). BE 도 "첫 구간" 을 이렇게 본다.
 * g당 단가 구간이면 기준 요금이 무게에 따라 달라 미리보기를 만들 수 없으므로 baseFee 를 null 로 둔다.
 */
export const firstRateBasis = (rates: ReadonlyArray<OverseasShippingRate>) => {
  if (rates.length === 0) return { baseFee: null, upperBound: null };
  const first = rates.reduce((a, b) => (b.weightFromKg < a.weightFromKg ? b : a));
  const perGram = first.perGramRateKrw != null && first.perGramRateKrw > 0;
  return { baseFee: perGram ? null : first.feeKrw, upperBound: first.weightToKg };
};

/** 권역 선택지에 곁들일 국가 수. 권역 번호만으로는 운영자가 알아보기 어렵다. */
const ZONE_COUNTRY_PREVIEW = 4;

/**
 * 권역 선택지 라벨 — "15 — US, CA" 처럼 그 권역에 속한 국가를 몇 개 붙인다.
 *
 * 국가는 이 정책의 국가그룹에서 가져온다(그 권역 번호를 쓰는 그룹들의 countryCodes). 이 정책에 없는
 * 권역은 번호만 보인다. BE 의 zone 목록 API 가 나오면 거기서 받은 국가로 바꾼다.
 */
export const emsZoneLabel = (zone: string, groups: ReadonlyArray<OverseasCountryGroup>): string => {
  const codes: string[] = [];
  for (const g of groups) {
    if (g.groupName !== zone) continue;
    for (const c of g.countryCodes) if (!codes.includes(c)) codes.push(c);
  }
  if (codes.length === 0) return zone;
  const head = codes.slice(0, ZONE_COUNTRY_PREVIEW).join(', ');
  return `${zone} — ${head}${codes.length > ZONE_COUNTRY_PREVIEW ? ', …' : ''}`;
};

/** 쉼표/공백 구분 국가코드 입력을 ISO2 배열로 — 대문자, 중복/빈값 제거. */
const parseCountryCodes = (text: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of text.split(/[\s,]+/)) {
    const code = token.trim().toUpperCase();
    if (code === '' || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
};

/** number 입력 파싱 — 빈/NaN 은 0. */
const num = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * R4 163 — per-gram 단가 파싱. 빈 문자열 = null(정액만 사용, BE 미전송 시맨틱), 그 외 유효숫자.
 * 0/음수/NaN 도 null 로(>0 만 per-gram 으로 의미 — feeKrw 와 둘 다 비면 BE 400 가드).
 */
const numOrNull = (v: string): number | null => {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * 국가 그룹 동적 테이블. 각 그룹은 1:1 매칭(그룹명/조건명) + 무게구간 요율표를 가진다.
 * basis=EMS 면 요율은 EMS 표준 시드를 따르므로 SELF 요율 입력을 숨긴다.
 */
export const CountryGroupEditor = ({ groups, basis, disabled, onChange }: CountryGroupEditorProps) => {
  const { t } = useTranslation('overseasShipping');
  const isSelf = basis === 'SELF';

  const updateGroup = (index: number, patch: Partial<OverseasCountryGroup>) => {
    onChange(groups.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  };

  const addGroup = () => onChange([...groups, emptyGroup()]);

  const removeGroup = (index: number) => onChange(groups.filter((_, i) => i !== index));

  const updateRate = (gi: number, ri: number, patch: Partial<OverseasShippingRate>) => {
    const group = groups[gi];
    if (!group) return;
    const rates = group.rates.map((r, i) => (i === ri ? { ...r, ...patch } : r));
    updateGroup(gi, { rates });
  };

  const addRate = (gi: number) => {
    const group = groups[gi];
    if (!group) return;
    updateGroup(gi, { rates: [...group.rates, emptyRate()] });
  };

  const removeRate = (gi: number, ri: number) => {
    const group = groups[gi];
    if (!group) return;
    updateGroup(gi, { rates: group.rates.filter((_, i) => i !== ri) });
  };

  return (
    <div className="flex flex-col gap-3">
      {groups.length === 0 ? (
        <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {t('overseasShipping.groupEditor.empty')}
        </p>
      ) : null}

      {groups.map((group, gi) => (
        <div
          key={group.groupId ?? `new-${gi}`}
          className="flex flex-col gap-3 rounded-md p-3"
          style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
        >
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                {isSelf
                  ? t('overseasShipping.groupEditor.groupName')
                  : t('overseasShipping.groupEditor.emsZone')}
                <RequiredMark />
              </span>
              {isSelf ? (
                <Input
                  value={group.groupName}
                  onChange={(e) => updateGroup(gi, { groupName: e.target.value })}
                  placeholder={t('overseasShipping.groupEditor.groupNamePlaceholder')}
                  disabled={disabled}
                  className="max-w-[220px]"
                />
              ) : (
                <RadixSelect
                  // 목록에 없는 값이어도 비우지 않고 그대로 보인다. 예전에는 빈 칸으로 보여서, 운영자가
                  // 무심코 다른 값을 고르면 이관된 권역이 조용히 바뀌었다. 저장 시 검증이 따로 막는다.
                  value={group.groupName}
                  onChange={(v) => updateGroup(gi, { groupName: v })}
                  options={[
                    ...EMS_ZONES.map((z) => ({ value: z, label: emsZoneLabel(z, groups) })),
                    ...(group.groupName !== '' && !isEmsZone(group.groupName)
                      ? [{
                          value: group.groupName,
                          label: t('overseasShipping.groupEditor.emsZoneUnknown', {
                            zone: group.groupName,
                            defaultValue: '{{zone}} (요율표에 없음)',
                          }),
                        }]
                      : []),
                  ]}
                  placeholder={t('overseasShipping.groupEditor.emsZonePlaceholder')}
                  disabled={disabled}
                  aria-label={t('overseasShipping.groupEditor.emsZone')}
                  className="max-w-[220px]"
                />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                {t('overseasShipping.groupEditor.conditionName')}
              </span>
              <Input
                value={group.conditionName}
                onChange={(e) => updateGroup(gi, { conditionName: e.target.value })}
                placeholder={t('overseasShipping.groupEditor.conditionNamePlaceholder')}
                disabled={disabled}
                className="max-w-[220px]"
              />
            </div>
            {!disabled ? (
              <Button
                variant="danger"
                size="sm"
                className="ml-auto"
                onClick={() => removeGroup(gi)}
                aria-label={t('overseasShipping.groupEditor.removeGroup')}
              >
                <Trash2 size={14} aria-hidden="true" />
                {t('overseasShipping.groupEditor.removeGroup')}
              </Button>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('overseasShipping.groupEditor.countryCodes')}
            </span>
            <Input
              value={group.countryCodes.join(', ')}
              onChange={(e) => updateGroup(gi, { countryCodes: parseCountryCodes(e.target.value) })}
              placeholder={t('overseasShipping.groupEditor.countryCodesPlaceholder')}
              disabled={disabled}
              className="max-w-[360px]"
              aria-label={t('overseasShipping.groupEditor.countryCodes')}
            />
            <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
              {t('overseasShipping.groupEditor.countryCodesHint')}
            </p>
          </div>

          {/* SELF 요율표 (EMS 는 표준요율이라 입력 숨김) */}
          {isSelf ? (
            <div className="flex flex-col gap-2">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                {t('overseasShipping.groupEditor.rateTitle')}
              </span>
              <div className="overflow-hidden rounded-md" style={{ border: '1px solid var(--divider-soft)' }}>
                <table className="w-full border-collapse text-[12px]">
                  <thead>
                    <tr style={{ background: 'var(--surface-raised)' }}>
                      <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>
                        {t('overseasShipping.groupEditor.colWeightFrom')}
                      </th>
                      <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>
                        {t('overseasShipping.groupEditor.colWeightTo')}
                      </th>
                      <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>
                        {t('overseasShipping.groupEditor.colFee', { defaultValue: '정액(원)' })}
                      </th>
                      {/* R4 163 — g당 소수점 단가(원/g). 정액과 택1/병기. */}
                      <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>
                        {t('overseasShipping.groupEditor.colPerGram', { defaultValue: 'g당 단가(원/g)' })}
                      </th>
                      <th className="w-[44px] px-2 py-2" aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {group.rates.map((rate, ri) => (
                      <tr key={rate.id ?? `new-${ri}`} style={{ borderTop: '1px solid var(--divider-soft)' }}>
                        <td className="px-2 py-1.5">
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={String(rate.weightFromKg)}
                            onChange={(e) => updateRate(gi, ri, { weightFromKg: num(e.target.value) })}
                            disabled={disabled}
                            className="h-8 tabular-nums"
                            aria-label={t('overseasShipping.groupEditor.weightFromAria')}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={String(rate.weightToKg)}
                            onChange={(e) => updateRate(gi, ri, { weightToKg: num(e.target.value) })}
                            disabled={disabled}
                            className="h-8 tabular-nums"
                            aria-label={t('overseasShipping.groupEditor.weightToAria')}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            type="number"
                            step="1"
                            min={0}
                            value={String(rate.feeKrw)}
                            onChange={(e) => updateRate(gi, ri, { feeKrw: num(e.target.value) })}
                            disabled={disabled}
                            className="h-8 tabular-nums"
                            aria-label={t('overseasShipping.groupEditor.feeAria')}
                          />
                        </td>
                        {/* R4 163 — g당 소수점 단가. 빈값=null(정액만). 값(>0) 있으면 BE 가 무게×단가 산정.
                            소수 허용(step 0.0001, DECIMAL(10,4)). feeKrw·perGram 둘 다 0/빈값이면 BE 400. */}
                        <td className="px-2 py-1.5">
                          <Input
                            type="number"
                            step="0.0001"
                            min={0}
                            value={rate.perGramRateKrw != null ? String(rate.perGramRateKrw) : ''}
                            onChange={(e) => updateRate(gi, ri, { perGramRateKrw: numOrNull(e.target.value) })}
                            disabled={disabled}
                            placeholder={t('overseasShipping.groupEditor.perGramPlaceholder', { defaultValue: '예: 1.5' })}
                            className="h-8 tabular-nums"
                            aria-label={t('overseasShipping.groupEditor.perGramAria', { defaultValue: 'g당 단가(원/g)' })}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {!disabled && group.rates.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => removeRate(gi, ri)}
                              aria-label={t('overseasShipping.groupEditor.removeRate')}
                              style={{ color: 'var(--signal-danger)', cursor: 'pointer' }}
                            >
                              <Trash2 size={14} />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* 고도몰 배송조건 "범위반복"(order V61). 자체요율 전용 — EMS 면 BE 가 무시하므로 이 블록째 숨는다. */}
              <RangeRepeatFields
                value={group.rangeRepeat ?? null}
                onChange={(rangeRepeat) => updateGroup(gi, { rangeRepeat })}
                unitLabel="kg"
                baseFee={firstRateBasis(group.rates).baseFee}
                firstUpperBound={firstRateBasis(group.rates).upperBound}
                hasTiers={group.rates.length > 0}
                disabled={disabled}
                labelPrefix={`${group.groupName || String(gi + 1)} · `}
              />
              {/* R4 163 — 입력 규칙 안내(BE 400 가드 사전 표면화). */}
              <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                {t('overseasShipping.groupEditor.perGramHint', {
                  defaultValue: '요율 행마다 정액(원) 또는 g당 단가(원/g) 중 최소 하나를 입력하세요. g당 단가를 쓰면 요금은 무게 × 단가로 계산됩니다(소수 가능).',
                })}
              </p>
              {!disabled ? (
                <div>
                  <Button variant="outline" size="sm" onClick={() => addRate(gi)}>
                    <Plus size={14} aria-hidden="true" />
                    {t('overseasShipping.groupEditor.addRate')}
                  </Button>
                </div>
              ) : null}

              {/* 지역 추가배송비(order V62) — 자체요율 전용. 예) 중문몰 "한국" 그룹의 제주·도서산간. */}
              <div className="flex flex-col gap-2 pt-1">
                <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                  {t('overseasShipping.groupEditor.regionSurchargeTitle', { defaultValue: '지역 추가배송비' })}
                </span>
                <RegionSurchargeTable
                  rows={(group.regionSurcharges ?? []).map((r) => ({
                    ...(r.id !== undefined ? { id: r.id } : {}),
                    areaName: r.areaName,
                    fee: r.surchargeKrw,
                  }))}
                  onChange={(rows) =>
                    updateGroup(gi, {
                      regionSurcharges: rows.map((r) => ({
                        ...(r.id !== undefined ? { id: r.id } : {}),
                        areaName: r.areaName,
                        surchargeKrw: r.fee,
                      })),
                    })
                  }
                  disabled={disabled}
                  labelPrefix={`${group.groupName || String(gi + 1)} · `}
                  testId={`overseas-region-surcharge-${gi}`}
                />
              </div>
            </div>
          ) : (
            <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
              {t('overseasShipping.groupEditor.emsNotice')}
            </p>
          )}
        </div>
      ))}

      {!disabled ? (
        <div>
          <Button variant="outline" size="sm" onClick={addGroup} data-testid="overseas-add-group">
            <Plus size={15} aria-hidden="true" />
            {t('overseasShipping.groupEditor.addGroup')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
